import { AgmCoreModule } from '@agm/core'
import { CommonModule } from '@angular/common'
import {
  HttpClient,
  provideHttpClient,
  withInterceptors
} from '@angular/common/http'
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode
} from '@angular/core'
import { provideClientHydration } from '@angular/platform-browser'
import { provideAnimations } from '@angular/platform-browser/animations'
import {
  IsActiveMatchOptions,
  provideRouter,
  Router,
  withViewTransitions
} from '@angular/router'
import { provideServiceWorker } from '@angular/service-worker'
import { IntersectionObserverModule } from '@ng-web-apis/intersection-observer'
import { MutationObserverModule } from '@ng-web-apis/mutation-observer'
import { TranslateLoader, TranslateModule } from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import player from 'lottie-web'
import { CookieModule } from 'ngx-cookie'
import { provideLottieOptions } from 'ngx-lottie'
import { provideEnvironmentNgxMask } from 'ngx-mask'
import { environment } from '../environments/environment'
import { intercept } from './@core/auth/interceptors/auth.interceptor'
import { PwaService } from './@core/services/pwa/pwa.service'
import { GlobalErrorHandlerService } from './@core/services/error-handler/global-error-handler.service'
import { ThemeModule } from './@theme/theme.module'
import { routes } from './app.routes'
import { GraphQLModule } from './graphql.module'

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json')
}

export function setupTrustedTypes() {
  return () => {
    const trustedTypes = (
      window as unknown as {
        trustedTypes?: {
          createPolicy(
            name: string,
            rules: { createScriptURL(url: string): string }
          ): unknown
        }
      }
    ).trustedTypes
    if (trustedTypes) {
      trustedTypes.createPolicy('angular-service-worker', {
        createScriptURL: (url: string) => {
          if (url.startsWith('/') || url.startsWith(window.location.origin)) {
            return url
          }
          throw new Error(`Untrusted script URL: ${url}`)
        }
      })
    }
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    CommonModule,
    provideHttpClient(withInterceptors([intercept])),
    {
      provide: APP_INITIALIZER,
      useFactory: setupTrustedTypes,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (pwaService: PwaService) => () => {
        console.log('Inicializando PWA Service...')
        return Promise.resolve()
      },
      deps: [PwaService],
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService
    },
    provideAnimations(),
    provideEnvironmentNgxMask(),
    importProvidersFrom(
      AgmCoreModule.forRoot({
        apiKey: 'AIzaSyBMivUU5xlGsz8_HQZmPwR3rg4EWcDRfsw'
      })
    ),
    provideLottieOptions({
      player: () => player
    }),
    provideRouter(
      routes,
      withViewTransitions({
        onViewTransitionCreated: ({ transition }) => {
          const router = inject(Router)
          const targetUrl = router.getCurrentNavigation()!.finalUrl!
          // Skip the transition if the only thing
          // changing is the fragment and queryParams
          const options: IsActiveMatchOptions = {
            paths: 'exact',
            matrixParams: 'exact',
            fragment: 'ignored',
            queryParams: 'ignored'
          }
          if (router.isActive(targetUrl, options)) transition.skipTransition()
        }
      })
    ),
    provideClientHydration(),
    importProvidersFrom(ThemeModule.forRoot()),
    importProvidersFrom(MutationObserverModule),
    importProvidersFrom(IntersectionObserverModule),
    importProvidersFrom(GraphQLModule),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient]
        }
      })
    ),
    importProvidersFrom(
      CookieModule.withOptions({
        domain: environment.cookieDomain,
        path: '/',
        expires: 'session'
      })
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode() || environment.enableServiceWorkerInDev,
      registrationStrategy: 'registerImmediately'
    })
  ]
}
